import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import agentService from '../../../services/agentService'
import { authService } from '../../../services/authService'
import { useAuthStore } from '../../../store/authStore'
import { rememberActiveSession, sessionRouteId, storedSessionRouteId } from '../utils/sessionRouting'

async function loadSession(routeId, { anonymousToken } = {}) {
  try {
    return await agentService.getSession(routeId)
  } catch (error) {
    if (error?.response?.status !== 404 || !anonymousToken) throw error
    return agentService.getSession(routeId)
  }
}

export function useAgentSession() {
  const { sessionId: routeSessionId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, setSession: setAuthSession, logout } = useAuthStore()
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [stage, setStage] = useState('idle')

  const navigateToSession = useCallback((nextSession, { replace = true } = {}) => {
    const routeId = sessionRouteId(nextSession)
    if (!routeId) return
    rememberActiveSession(nextSession, user?.id)
    navigate(`/app/s/${routeId}`, { replace })
  }, [navigate, user?.id])

  const applySession = useCallback((nextSession) => {
    setSession(nextSession)
    rememberActiveSession(nextSession, user?.id)
  }, [user?.id])

  const refreshSession = useCallback(async (sid) => {
    const routeId = sid || sessionRouteId(session)
    if (!routeId) return
    const fresh = await agentService.getSession(routeId)
    applySession(fresh)
  }, [session, applySession])

  const startNewSession = useCallback(async ({ userIntent = null } = {}) => {
    const created = await agentService.createSession({ user_intent: userIntent, conversation_mode: 'mixed' })
    applySession(created)
    navigateToSession(created)
    return created
  }, [applySession, navigateToSession])

  const openSession = useCallback(async (routeId) => {
    const existing = await loadSession(routeId)
    applySession(existing)
    navigateToSession(existing)
    return existing
  }, [applySession, navigateToSession])

  useEffect(() => {
    const boot = async () => {
      const startedAt = Date.now()
      const onLanding = location.pathname === '/'
      try {
        const auth = await authService.getSession()
        if (auth.user) {
          setAuthSession(auth)
        } else {
          logout()
        }

        const userId = auth.user?.id

        if (routeSessionId) {
          try {
            const existing = await loadSession(routeSessionId, {
              anonymousToken: agentService.getStoredToken(routeSessionId),
            })
            applySession(existing)
            if (existing.public_id && routeSessionId !== existing.public_id) {
              navigate(`/app/s/${existing.public_id}`, { replace: true })
            }
          } catch (error) {
            if (error?.response?.status === 404) {
              if (userId) {
                try {
                  const resumed = await agentService.resumeSession()
                  applySession(resumed)
                  if (!onLanding) navigateToSession(resumed)
                } catch {
                  if (!onLanding) await startNewSession()
                }
              } else {
                const stored = storedSessionRouteId(null)
                if (stored) {
                  try {
                    const resumed = await agentService.resumeSession({
                      anonymousToken: agentService.getStoredToken(stored),
                    })
                    applySession(resumed)
                    if (!onLanding) navigateToSession(resumed)
                  } catch {
                    await startNewSession()
                  }
                } else if (!onLanding) {
                  await startNewSession()
                }
              }
            } else {
              throw error
            }
          }
        } else if (userId) {
          try {
            const resumed = await agentService.resumeSession()
            applySession(resumed)
            if (!onLanding) navigateToSession(resumed)
          } catch {
            const stored = storedSessionRouteId(userId)
            if (stored) {
              try {
                const existing = await loadSession(stored)
                applySession(existing)
                if (!onLanding) navigateToSession(existing)
              } catch {
                // Wait for explicit new session or landing enter
              }
            }
          }
        } else {
          const stored = storedSessionRouteId(null)
          if (stored) {
            try {
              const resumed = await agentService.resumeSession({
                anonymousToken: agentService.getStoredToken(stored),
              })
              applySession(resumed)
              if (!onLanding) navigateToSession(resumed)
            } catch {
              // Anonymous users start on landing without auto-creating
            }
          }
        }
      } finally {
        const minBootstrapMs = 700
        const elapsed = Date.now() - startedAt
        setTimeout(() => setLoading(false), Math.max(0, minBootstrapMs - elapsed))
      }
    }
    boot()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- boot only on route/auth surface change
  }, [routeSessionId, location.pathname])

  const submitMessage = useCallback(async (raw, { onChapterChange } = {}) => {
    const message = raw.trim()
    if (!message || busy) return
    setBusy(true)
    setStage('thinking')
    onChapterChange?.('index')
    try {
      let activeSession = session
      if (!activeSession) {
        activeSession = await startNewSession()
      }
      // Optimistically append the user turn so pending UI cards (repo picker, etc.)
      // clear immediately instead of reopening while the agent request is in flight.
      applySession({
        ...activeSession,
        messages: [
          ...(activeSession.messages || []),
          {
            id: `optimistic-${Date.now()}`,
            role: 'user',
            content: message,
            created_at: new Date().toISOString(),
          },
        ],
      })
      const data = await agentService.sendMessage(sessionRouteId(activeSession), message)
      if (data?.session) applySession(data.session)
      setStage('idle')
      return data
    } finally {
      setBusy(false)
    }
  }, [session, busy, applySession, startNewSession])

  const sendSensitiveInput = useCallback(async (field, value, options) => {
    let activeSession = session
    if (!activeSession) {
      activeSession = await startNewSession()
    }
    const routeId = sessionRouteId(activeSession)
    await agentService.sendSecureInput(routeId, field, value)
    const followUps = {
      password: 'I submitted my password via the secure field. Please continue sign-in.',
      otp: 'I submitted my verification code via the secure field. Please continue verification.',
    }
    return submitMessage(followUps[field] || 'I submitted secure input. Please continue.', options)
  }, [session, startNewSession, submitMessage])

  return {
    session,
    setSession: applySession,
    loading,
    busy,
    stage,
    setStage,
    refreshSession,
    submitMessage,
    sendSensitiveInput,
    startNewSession,
    openSession,
    sessionRouteId: session ? sessionRouteId(session) : null,
  }
}
